import { ChildData, ChildUpdateInfo, ClientRootType, ErrorMessage, IClientTypeSpecific, ModelUpdate, MChildrenList, MChildWithParentType, MIVConstants, MRealObject, PlaceholderChild, QueryBuilder, StringRealProperty, LongVirtualProperty, IRealObject, LongRealProperty, BinaryRealProperty, EnumRealProperty, MTargetsList, StringVirtualProperty, VirtualList, MVirtualObject, UpdateWatcher, MChild } from "@1oo4/miv";
import { DocClient } from "../docclient";
import { MRootDoc } from "./masterdata";
import { VSectionEdit } from "../../pages/edit/views";
import { VCellContentEdit, VCellEdit, VCellPictureEdit, VCellTextEdit, VCodeEdit, VContentEdit, VPictureEdit, VTableEdit, VTextEdit } from "../../pages/edit/contents";
import { DocIDS } from "../ids";
import { VSectionDoc } from "../../pages/doc/views";
import { VCode, VContent, VPicture, VTable, VText } from "../../pages/doc/contents";
import { MRichTextItem } from "./richtext";
import { VCell, VCellContent, VCellPicture, VCellText } from "../../pages/doc/tables";

export abstract class MDocLevel<P extends MRealObject> extends MChildWithParentType<P> {
    public readonly NumberIndexing: LongVirtualProperty;
    public readonly FullIndex: StringVirtualProperty;
    public readonly Name: StringRealProperty;

    constructor(data: ChildData) {
        super(data);
        this.NumberIndexing = new LongVirtualProperty(this, undefined);
        this.FullIndex = new StringVirtualProperty(this, undefined);
        this.Name = new StringRealProperty(this, MIVConstants.PROPERTY_NAME, "name");
    }

    public abstract MoveUpAsync(): Promise<ErrorMessage | undefined>;
    public abstract MoveDownAsync(): Promise<ErrorMessage | undefined>;
}

async function moveUpAsync<C extends MChildWithParentType<P>, P extends MRealObject>(c: C, list: MChildrenList<C>): Promise<ErrorMessage | undefined> {
    const qb = await c.Client.BeginQueryAsync();
    let prv: C | undefined = undefined;
    for (let i = 0; i < list.Count; i++) {
        const x = list.Get(i);
        if (x == c) {
            if (prv === undefined) {
                break;
            } else {
                qb.ReassignChildBefore(c, prv, MIVConstants.PROPERTY_ANY);
                return await qb.ExecuteAsync();
            }
        } else {
            prv = x;
        }
    }
    qb.Cancel();
    return undefined;
}
async function moveDownAsync<C extends MChildWithParentType<P>, P extends MRealObject>(c: C, list: MChildrenList<C>): Promise<ErrorMessage | undefined> {
    const qb = await c.Client.BeginQueryAsync();
    let found = false;
    for (let i = 0; i < list.Count; i++) {
        const x = list.Get(i);
        if (found) {
            qb.ReassignChildAfter(c, x, MIVConstants.PROPERTY_ANY);
            return await qb.ExecuteAsync();
        } else if (x == c) {
            found = true;
        }
    }
    qb.Cancel();
    return undefined;
}

export class MChapter extends MDocLevel<MRootDoc> {
    public readonly Articles: MChildrenList<MArticle>;

    constructor(data: ChildData, client: DocClient) {
        super(data);
        this.Articles = new MChildrenList<MArticle>(this, MIVConstants.PROPERTY_ANY, client.articles);
    }

    GetParentType(): ClientRootType {
        return (this.Client as DocClient).DocRoot.Type;
    }

    protected Updated(mu: ModelUpdate, movement: ChildUpdateInfo | undefined): void {
        if (mu.IsListChanged(this.Articles)) {
            this.Parent.mustUpdateIndexing(mu);
            for (let i = 0; i < this.Articles.Count; i++) {
                mu.SetDefaultLong(this.Articles.Get(i).NumberIndexing, BigInt(i + 1));
            }
        }
    }

    public async MoveUpAsync(): Promise<ErrorMessage | undefined> {
        return moveUpAsync(this, this.Parent.Chapters);
    }
    public async MoveDownAsync(): Promise<ErrorMessage | undefined> {
        return moveDownAsync(this, this.Parent.Chapters);
    }

    public AppendArticleAsync(): Promise<MArticle | undefined> {
        return this.AppendChildAsync((this.Client as DocClient).articles, MIVConstants.PROPERTY_ANY);
    }
}

export class MArticle extends MDocLevel<MChapter> {
    public readonly Sections: MChildrenList<MSection>;

    constructor(data: ChildData, client: DocClient) {
        super(data);
        this.Sections = new MChildrenList<MSection>(this, MIVConstants.PROPERTY_ANY, client.sections);
    }

    GetParentType(): IClientTypeSpecific<MChapter> {
        return (this.Client as DocClient).chapters;
    }

    protected Updated(mu: ModelUpdate, movement: ChildUpdateInfo | undefined): void {
        if (mu.IsListChanged(this.Sections)) {
            this.Parent.Parent.mustUpdateIndexing(mu);
            for (let i = 0; i < this.Sections.Count; i++) {
                mu.SetDefaultLong(this.Sections.Get(i).NumberIndexing, BigInt(i + 1));
            }
        }
    }

    public async MoveUpAsync(): Promise<ErrorMessage | undefined> {
        return moveUpAsync(this, this.Parent.Articles);
    }
    public async MoveDownAsync(): Promise<ErrorMessage | undefined> {
        return moveDownAsync(this, this.Parent.Articles);
    }

    public AppendSectionAsync(): Promise<MSection | undefined> {
        return this.AppendChildAsync((this.Client as DocClient).sections, MIVConstants.PROPERTY_ANY);
    }

    public async LoadSectionsAndUpdateUIAsync(): Promise<ErrorMessage | undefined> {
        const token = await this.Client.TakeLockAsync();
        const error = await this.LoadChildrenAsync((this.Client as DocClient).fullLoad, token);
        if (error !== undefined) {
            return error;
        }
        await this.Client.ExecuteViewUpdateAsync(token);
        this.Client.ReleaseLock();
    }
}

export class MSection extends MDocLevel<MArticle> {

    public readonly Contents: MChildrenList<MContent>;

    constructor(data: ChildData, client: DocClient) {
        super(data);
        this.Contents = new MChildrenList<MContent>(this, MIVConstants.PROPERTY_ANY, client.contents);
    }

    GetParentType(): IClientTypeSpecific<MArticle> {
        return (this.Client as DocClient).articles;
    }

    public async MoveUpAsync(): Promise<ErrorMessage | undefined> {
        return moveUpAsync(this, this.Parent.Sections);
    }
    public async MoveDownAsync(): Promise<ErrorMessage | undefined> {
        return moveDownAsync(this, this.Parent.Sections);
    }

    public async LoadContentsAndUpdateUIAsync(): Promise<ErrorMessage | undefined> {
        const token = await this.Client.TakeLockAsync();
        const error = await this.LoadChildrenAsync((this.Client as DocClient).fullLoad, token);
        if (error !== undefined) {
            return error;
        }
        await this.Client.ExecuteViewUpdateAsync(token);
        this.Client.ReleaseLock();
    }

    public AppendTextAsync(): Promise<MText | undefined> {
        return this.AppendChildAsync((this.Client as DocClient).texts, MIVConstants.PROPERTY_ANY);
    }
    public async AppendCodeAsync(): Promise<MCode | undefined> {
        const qb = await this.Client.BeginQueryAsync();
        const code = qb.AppendChild(this, (this.Client as DocClient).codes, MIVConstants.PROPERTY_ANY);
        qb.AppendChild(code, (this.Client as DocClient).codeTabs, MIVConstants.PROPERTY_ANY);
        await qb.ExecuteAsync();
        return code.Result;
    }
    public AppendImageAsync(): Promise<MPicture | undefined> {
        return this.AppendChildAsync((this.Client as DocClient).pictures, MIVConstants.PROPERTY_ANY);
    }
    private static appendColOnNewTable(table: PlaceholderChild<MTable>, qb: QueryBuilder) {
        const col = qb.AppendChild(table, (qb.Client as DocClient).columns, MIVConstants.PROPERTY_ANY);
        MTable.appendCell(col, qb);
        MTable.appendCell(col, qb);
    }
    public async AppendTable2x2Async(): Promise<MTable | undefined> {
        const qb = await this.Client.BeginQueryAsync();
        const table = qb.AppendChild(this, (this.Client as DocClient).tables, MIVConstants.PROPERTY_ANY);
        MSection.appendColOnNewTable(table, qb);
        MSection.appendColOnNewTable(table, qb);
        await qb.ExecuteAsync();
        return table.Result;
    }
}

export abstract class MContent extends MChildWithParentType<MSection> {

    public readonly LanguageFlag: CodeLanguageProperty;

    constructor(data: ChildData) {
        super(data);
        this.LanguageFlag = new CodeLanguageProperty(this);
    }

    GetParentType(): IClientTypeSpecific<MSection> {
        return (this.Client as DocClient).sections;
    }

    public async MoveUpAsync(): Promise<ErrorMessage | undefined> {
        return moveUpAsync(this, this.Parent.Contents);
    }
    public async MoveDownAsync(): Promise<ErrorMessage | undefined> {
        return moveDownAsync(this, this.Parent.Contents);
    }

    abstract makeViewEdit(parent: VSectionEdit): VContentEdit;
    abstract makeViewDoc(parent: VSectionDoc): VContent;

    abstract toHTML(): string;
    abstract toMarkdown(): string;
}

export abstract class MContentWithText extends MContent {

    public readonly ChapterRefs: MTargetsList<MChapter>;
    public readonly ArticleRefs: MTargetsList<MArticle>;
    public readonly SectionRefs: MTargetsList<MSection>;

    constructor(data: ChildData) {
        super(data);
        this.ChapterRefs = new MTargetsList<MChapter>(this, MIVConstants.PROPERTY_GENERIC_RELATION, (this.Client as DocClient).chapters);
        this.ArticleRefs = new MTargetsList<MArticle>(this, MIVConstants.PROPERTY_GENERIC_RELATION, (this.Client as DocClient).articles);
        this.SectionRefs = new MTargetsList<MSection>(this, MIVConstants.PROPERTY_GENERIC_RELATION, (this.Client as DocClient).sections);
    }
}

export class MText extends MContentWithText {

    public readonly Text: StringRealProperty;
    public readonly RichText: VirtualList<MRichTextItem>;

    constructor(data: ChildData) {
        super(data);
        this.Text = new StringRealProperty(this, DocIDS.PR_TEXT, "text");
        this.RichText = new VirtualList(this);
    }

    protected Updated(mu: ModelUpdate, movement: ChildUpdateInfo | undefined): void {
        if (mu.IsPropertyChanged(this.Text)) {
            (this.Client as DocClient).richParser.parseM(this.Text.Value, this, this.RichText, mu);
        }
    }

    makeViewEdit(parent: VSectionEdit): VTextEdit {
        return new VTextEdit(this, parent);
    }
    makeViewDoc(parent: VSectionDoc): VContent {
        return new VText(this, parent);
    }

    toHTML(): string {
        let html = "<p>";
        for (let i = 0; i < this.RichText.Count; i++) {
            html += this.RichText.Get(i).toHTML();
        }
        return html + "</p>";
    }

    toMarkdown(): string {
        let md = '\n\n';
        for (let i = 0; i < this.RichText.Count; i++) {
            md += this.RichText.Get(i).toMarkdown(false);
        }
        return md;
    }
}

export class MPicture extends MContent {

    public readonly Data: BinaryRealProperty;

    constructor(data: ChildData) {
        super(data);
        this.Data = new BinaryRealProperty(this, DocIDS.PR_DATA, "data");
    }

    makeViewEdit(parent: VSectionEdit): VPictureEdit {
        return new VPictureEdit(this, parent);
    }
    makeViewDoc(parent: VSectionDoc): VContent {
        return new VPicture(this, parent);
    }

    toHTML(): string {
        // todo include images
        return "";
    }
    toMarkdown(): string {
        // todo include images
        return "";
    }
}

export enum CodeLanguage {
    PSEUDO = 0, CS = 1, TS = 2, XAML = 3, SQL = 4, JSON = 5, HTML = 6, CSS = 7
}

export class CodeLanguageProperty extends EnumRealProperty {

    constructor(owner: MRealObject) {
        super(owner, DocIDS.PR_CODE_LANGUAGE, "language");
    }

    public get Value(): CodeLanguage { return this.ValueByte as CodeLanguage; }

    public SetValueAsync(value: CodeLanguage): Promise<ErrorMessage | undefined> {
        return this.SetByteValueAsync(value, false);
    }
}

export class MCode extends MContent {
    public readonly Tabs: MChildrenList<MCodeTab>;

    constructor(data: ChildData) {
        super(data);
        this.Tabs = new MChildrenList(this, MIVConstants.PROPERTY_ANY, (this.Client as DocClient).codeTabs);
    }

    public async AppendTabAsync(): Promise<MCodeTab | undefined> {
        return this.AppendChildAsync((this.Client as DocClient).codeTabs, MIVConstants.PROPERTY_ANY);
    }

    makeViewEdit(parent: VSectionEdit): VContentEdit {
        return new VCodeEdit(this, parent);
    }
    makeViewDoc(parent: VSectionDoc): VContent {
        return new VCode(this, parent);
    }

    private static describeLanguageForExport(lang: CodeLanguage) {
        switch (lang) {
            case CodeLanguage.CS: return "csharp";
            case CodeLanguage.TS: return "typescript";
            case CodeLanguage.XAML: return "xaml";
            case CodeLanguage.HTML: return "html";
            case CodeLanguage.CSS: return "css";
            case CodeLanguage.SQL: return "sql";
            case CodeLanguage.JSON: return "json";
            default: return "pseudocode";
        }
    }
    toHTML(): string {
        let html = '<div>';
        for (let i = 0; i < this.Tabs.Count; i++) {
            const tab = this.Tabs.Get(i);
            html += '<pre><code language="' + MCode.describeLanguageForExport(tab.Language.Value) + '">' + tab.Code.Value + '</pre></code>';
        }
        return html + '</div>';
    }
    toMarkdown(): string {
        let md = '\n';
        for (let i = 0; i < this.Tabs.Count; i++) {
            const tab = this.Tabs.Get(i);
            md += '\n```' + MCode.describeLanguageForExport(tab.Language.Value) + '\n' + tab.Code.Value + '\n```';
        }
        return md;
    }
}

export class MCodeTab extends MChildWithParentType<MCode> {
    public readonly Code: StringRealProperty;
    public readonly Language: CodeLanguageProperty;

    constructor(data: ChildData) {
        super(data);
        this.Code = new StringRealProperty(this, DocIDS.PR_CODE, "code");
        this.Language = new CodeLanguageProperty(this);
    }
    GetParentType(): ClientRootType | IClientTypeSpecific<MCode> {
        return (this.Client as DocClient).codes;
    }
}

export class MTable extends MContentWithText {

    public readonly Columns: MChildrenList<MColumn>;
    public readonly Rows: VirtualList<MRow>;
    private _last_update_rows_doc_: number = 0;
    public readonly ColumnsChanged: UpdateWatcher;

    constructor(data: ChildData) {
        super(data);
        this.Columns = new MChildrenList<MColumn>(this, MIVConstants.PROPERTY_ANY, (data.Client as DocClient).columns);
        this.Rows = new VirtualList(this);
        this.ColumnsChanged = new UpdateWatcher();
    }

    /** @internal */
    _updateRows_doc_(mu: ModelUpdate) {
        if (mu.Counter != this._last_update_rows_doc_) {
            this._last_update_rows_doc_ = mu.Counter;
            let count = 0;
            for (let c = 0; c < this.Columns.Count; c++) {
                const col = this.Columns.Get(c);
                if (col.Cells.Count > count) count = col.Cells.Count;
            }
            const rowsList = (this.Client as DocClient).rowsList;
            const cellsList = (this.Client as DocClient).cellsList;
            for (let i = 0; i < count; i++) {
                let r: MRow;
                if (i < this.Rows.Count) {
                    r = this.Rows.Get(i);
                } else {
                    r = new MRow(this);
                }
                rowsList.push(r);
                for (let c = 0; c < this.Columns.Count; c++) {
                    const cell = this.Columns.Get(c).Cells.Get(i);
                    if (cell !== undefined) {
                        cellsList.push(cell);
                    }
                }
                mu.DefineArray(r.Cells, cellsList);
                cellsList.length = 0;
            }
            mu.DefineArray(this.Rows, rowsList);
            rowsList.length = 0;
        }
    }

    protected Updated(mu: ModelUpdate, movement: ChildUpdateInfo | undefined): void {
        if (mu.IsListChanged(this.Columns)) {
            this._updateRows_doc_(mu);
            this.ColumnsChanged.Trigger(mu);
        }
    }

    makeViewEdit(parent: VSectionEdit): VTableEdit {
        return new VTableEdit(this, parent);
    }
    makeViewDoc(parent: VSectionDoc): VContent {
        return new VTable(this, parent);
    }

    static appendCell(col: IRealObject, qb: QueryBuilder) {
        const cell = qb.AppendChild(col, (qb.Client as DocClient).cells, MIVConstants.PROPERTY_ANY);
        qb.AppendChild(cell, (qb.Client as DocClient).cellTexts, MIVConstants.PROPERTY_ANY);
    }

    public async AppendRowAsync(): Promise<ErrorMessage | undefined> {
        const qb = await this.Client.BeginQueryAsync();
        for (let c = 0; c < this.Columns.Count; c++) {
            MTable.appendCell(this.Columns.Get(c), qb);
        }
        return await qb.ExecuteAsync();
    }

    public async AppendColumnAsync(): Promise<MColumn | undefined> {
        const qb = await this.Client.BeginQueryAsync();
        const col = qb.AppendChild(this, (this.Client as DocClient).columns, MIVConstants.PROPERTY_ANY);
        const rows = this.Rows.Count;
        for (let i = 0; i < rows; i++) {
            MTable.appendCell(col, qb);
        }
        await qb.ExecuteAsync();
        return col.Result;
    }

    public async DeleteRowAsync(index: number): Promise<ErrorMessage | undefined> {
        const qb = await this.Client.BeginQueryAsync();
        if (index < this.Rows.Count) {
            for (let c = 0; c < this.Columns.Count; c++) {
                const col = this.Columns.Get(c);
                if (index < col.Cells.Count) {
                    qb.DeleteChild(col.Cells.Get(index));
                }
            }
            return await qb.ExecuteAsync();
        } else {
            qb.Cancel();
            return undefined;
        }
    }

    toHTML(): string {
        let html = '<table>';
        for (let i = 0; i < this.Rows.Count; i++) {
            const row = this.Rows.Get(i);
            html += '<tr>';
            for (let j = 0; j < row.Cells.Count; j++) {
                const cell = row.Cells.Get(j);
                html += '<td>';
                for (let k = 0; k < cell.Content.Count; k++) {
                    html += cell.Content.Get(k).toHTML();
                }
                html += '</td>';
            }
            html += '</tr>';
        }
        return html + '</table>'
    }
    toMarkdown(): string {
        let md = '\n';
        for (let i = 0; i < this.Rows.Count; i++) {
            const row = this.Rows.Get(i);
            md += '\n|';
            for (let j = 0; j < row.Cells.Count; j++) {
                const cell = row.Cells.Get(j);
                md += ' ';
                for (let k = 0; k < cell.Content.Count; k++) {
                    md += cell.Content.Get(k).toMarkdown();
                }
                md += ' |';
            }
            if (i == 0 && this.Rows.Count > 1) {
                md += '\n|';
                for (let j = 0; j < row.Cells.Count; j++) {
                    md += ' :---: |';
                }
            }
        }
        return md;
    }
}

export class MColumn extends MChildWithParentType<MTable> {

    public readonly Cells: MChildrenList<MCell>;
    public readonly Width: LongRealProperty;

    constructor(data: ChildData) {
        super(data);
        this.Cells = new MChildrenList<MCell>(this, MIVConstants.PROPERTY_ANY, (data.Client as DocClient).cells);
        this.Width = new LongRealProperty(this, DocIDS.PR_WIDTH, "width");
    }

    GetParentType(): IClientTypeSpecific<MTable> {
        return (this.Client as DocClient).tables;
    }

    protected Updated(mu: ModelUpdate, movement: ChildUpdateInfo | undefined): void {
        if (mu.IsListChanged(this.Cells)) {
            this.Parent._updateRows_doc_(mu);
        }
        if (mu.IsPropertyChanged(this.Width)) {
            this.Parent.ColumnsChanged.Trigger(mu);
        }
    }
}

export class MCell extends MChildWithParentType<MColumn> {

    public readonly Content: MChildrenList<MCellContent>;

    constructor(data: ChildData) {
        super(data);
        this.Content = new MChildrenList<MCellContent>(this, MIVConstants.PROPERTY_ANY, (this.Client as DocClient).cellContent);
    }
    GetParentType(): IClientTypeSpecific<MColumn> {
        return (this.Client as DocClient).columns;
    }
}

export abstract class MCellContent extends MChildWithParentType<MCell> {
    constructor(data: ChildData) {
        super(data);
    }
    GetParentType(): IClientTypeSpecific<MCell> {
        return (this.Client as DocClient).cells;
    }

    abstract addRichTextToList(allProps: StringRealProperty[]): void;

    abstract makeViewEdit(parent: VCellEdit): VCellContentEdit;
    abstract makeViewDoc(parent: VCell): VCellContent;

    abstract toHTML(): string;
    abstract toMarkdown(): string;
}

export class MCellText extends MCellContent {
    public readonly Text: StringRealProperty;
    public readonly RichText: VirtualList<MRichTextItem>;

    constructor(data: ChildData) {
        super(data);
        this.Text = new StringRealProperty(this, DocIDS.PR_TEXT, "text");
        this.RichText = new VirtualList(this);
    }
    makeViewEdit(parent: VCellEdit): VCellTextEdit {
        return new VCellTextEdit(this, parent);
    }
    makeViewDoc(parent: VCell): VCellContent {
        return new VCellText(this, parent);
    }

    addRichTextToList(allProps: StringRealProperty[]): void {
        allProps.push(this.Text);
    }

    protected Updated(mu: ModelUpdate, movement: ChildUpdateInfo | undefined): void {
        if (mu.IsPropertyChanged(this.Text)) {
            (this.Client as DocClient).richParser.parseM(this.Text.Value, this.Parent.Parent.Parent, this.RichText, mu);
        }
    }

    toHTML(): string {
        let html = '<p>';
        for (let i = 0; i < this.RichText.Count; i++) {
            html += this.RichText.Get(i).toHTML();
        }
        return html + '</p>';
    }
    toMarkdown(): string {
        let md = '';
        for (let i = 0; i < this.RichText.Count; i++) {
            md += this.RichText.Get(i).toMarkdown(true);
        }
        return md;
    }
}

export class MCellPicture extends MCellContent {
    readonly Data: BinaryRealProperty;

    constructor(data: ChildData) {
        super(data);
        this.Data = new BinaryRealProperty(this, DocIDS.PR_DATA, "data");
    }
    makeViewEdit(parent: VCellEdit): VCellPictureEdit {
        return new VCellPictureEdit(this, parent);
    }
    makeViewDoc(parent: VCell): VCellContent {
        return new VCellPicture(this, parent);
    }

    addRichTextToList(allProps: StringRealProperty[]): void {
        // rien
    }

    toHTML(): string {
        // TODO include images
        return '';
    }
    toMarkdown(): string {
        // TODO include images
        return '';
    }
}

export class MRow extends MVirtualObject {
    public readonly Cells: VirtualList<MCell>;

    constructor(table: MTable) {
        super(table);
        this.Cells = new VirtualList(this);
    }
}